export const INCOMING_CALL_SERVICE_WORKER_URL = '/incoming-call-sw.js';
export const INCOMING_CALL_NOTIFICATION_TAG = 'incoming-call';
export const INCOMING_CALL_NOTIFICATION_SOURCE = 'incoming-call-notification';

// The notification is shown with `requireInteraction: true` so an agent who is
// away from the tab still finds it on screen when they come back. The flip side
// is that nothing takes it down on its own, so every path that ends the ring has
// to close it explicitly: the call being answered or rejected here, being
// answered/cancelled on another extension of the ring group (both surface as the
// incoming call leaving the phone store), the agent coming back to the tab, the
// page going away — and, as a last resort, this timeout, for when the page never
// sees the end of the call (a dropped SIP WebSocket, for instance).
//
// 60s mirrors JsSIP's default `no_answer_timeout`, so the fallback never hides a
// call that is still legitimately ringing; it only bounds how long a stale
// notification can sit on the desktop.
export const INCOMING_CALL_NOTIFICATION_TIMEOUT_MS = 60_000;

export type IncomingCallNotificationAction = 'answer' | 'reject';

export type IncomingCallNotificationMessage = {
  source: typeof INCOMING_CALL_NOTIFICATION_SOURCE;
  action: IncomingCallNotificationAction;
};

export type IncomingCallNotificationActionDescriptor = {
  action: IncomingCallNotificationAction;
  title: string;
};

export const isIncomingCallNotificationMessage = (
  data: unknown
): data is IncomingCallNotificationMessage => {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as Record<string, unknown>;
  return (
    candidate.source === INCOMING_CALL_NOTIFICATION_SOURCE &&
    (candidate.action === 'answer' || candidate.action === 'reject')
  );
};

export type IncomingCallPermissionGrantedMessage = {
  source: typeof INCOMING_CALL_NOTIFICATION_SOURCE;
  type: 'permission-granted';
};

// Sent via window.postMessage by public/enable-notifications.html (opened as
// its own top-level tab, via window.opener) once the user grants the
// notification permission there. Not sent through the Service Worker, since
// this signals page-to-page, not click-on-notification.
export const isIncomingCallPermissionGrantedMessage = (
  data: unknown
): data is IncomingCallPermissionGrantedMessage => {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as Record<string, unknown>;
  return (
    candidate.source === INCOMING_CALL_NOTIFICATION_SOURCE &&
    candidate.type === 'permission-granted'
  );
};

// Deliberately does not check `Notification.permission`: read from inside a
// cross-origin iframe (this app's real deployment context, embedded in
// OMNIHIT), Chrome always reports it as 'denied' regardless of the actual
// per-origin grant — it's a synthetic, privacy-motivated value. The real
// permission is still enforced correctly by showNotification() itself, so we
// just attempt it and swallow the rejection when it's genuinely not granted.
export const shouldShowIncomingCallNotification = ({
  hasIncomingCall,
  visibilityState,
}: {
  hasIncomingCall: boolean;
  visibilityState: DocumentVisibilityState;
}): boolean => hasIncomingCall && visibilityState === 'hidden';

export const supportsNotificationActions = (): boolean =>
  typeof Notification !== 'undefined' && 'actions' in Notification.prototype;

export type IncomingCallNotificationContent = {
  callId: string;
  title: string;
  body: string;
  actions: IncomingCallNotificationActionDescriptor[];
};

export const buildIncomingCallNotificationContent = ({
  callId,
  title,
  callerLabel,
  answerLabel,
  rejectLabel,
  canReject,
}: {
  callId: string;
  title: string;
  callerLabel: string;
  answerLabel: string;
  rejectLabel: string;
  canReject: boolean;
}): IncomingCallNotificationContent => ({
  callId,
  title,
  body: callerLabel,
  actions: [
    { action: 'answer', title: answerLabel },
    ...(canReject
      ? [{ action: 'reject' as const, title: rejectLabel }]
      : []),
  ],
});

export const routeIncomingCallNotificationAction = (
  action: IncomingCallNotificationAction,
  {
    canReject,
    onAnswer,
    onReject,
  }: { canReject: boolean; onAnswer: () => void; onReject: () => void }
): void => {
  if (action === 'answer') {
    onAnswer();
    return;
  }

  if (action === 'reject' && canReject) {
    onReject();
  }
};

let registrationPromise:
  | Promise<ServiceWorkerRegistration | undefined>
  | undefined;

export const registerIncomingCallServiceWorker = (): Promise<
  ServiceWorkerRegistration | undefined
> => {
  if (!('serviceWorker' in navigator)) {
    return Promise.resolve(undefined);
  }

  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(INCOMING_CALL_SERVICE_WORKER_URL)
      .catch(() => undefined);
  }

  return registrationPromise;
};

export const listenForIncomingCallNotificationActions = (
  onAction: (action: IncomingCallNotificationAction) => void
): (() => void) => {
  if (!('serviceWorker' in navigator)) {
    return () => {};
  }

  const handleMessage = (event: MessageEvent) => {
    if (isIncomingCallNotificationMessage(event.data)) {
      onAction(event.data.action);
    }
  };

  navigator.serviceWorker.addEventListener('message', handleMessage);
  return () =>
    navigator.serviceWorker.removeEventListener('message', handleMessage);
};

export const showIncomingCallNotification = async ({
  callId,
  title,
  body,
  actions,
}: IncomingCallNotificationContent): Promise<void> => {
  const registration = await registerIncomingCallServiceWorker();
  if (!registration) return;

  try {
    await registration.showNotification(title, {
      body,
      tag: INCOMING_CALL_NOTIFICATION_TAG,
      requireInteraction: true,
      // Scopes every close request to the call it was shown for. The tag alone
      // is not enough: it is shared by every incoming call, so an in-flight
      // close — the service worker still booting, for instance — would take
      // down whichever notification happens to be on screen by the time it
      // runs, including one for a newer call.
      data: { callId },
      ...(supportsNotificationActions() ? { actions } : {}),
    });
  } catch {
    // Permission not actually granted for this origin — fail silently.
  }
};

export type IncomingCallNotificationCloseRequest = {
  source: typeof INCOMING_CALL_NOTIFICATION_SOURCE;
  type: 'close';
  callId: string;
};

export const buildIncomingCallNotificationCloseRequest = (
  callId: string
): IncomingCallNotificationCloseRequest => ({
  source: INCOMING_CALL_NOTIFICATION_SOURCE,
  type: 'close',
  callId,
});

export const notificationBelongsToCall = (
  notification: { data?: unknown },
  callId: string
): boolean => {
  const data = notification.data;
  if (typeof data !== 'object' || data === null) return false;
  return (data as Record<string, unknown>).callId === callId;
};

// Closing from the document only works in a first-party context (HitPhone
// opened standalone, public/enable-notifications.html). Kept as a best-effort
// path; the service worker below is what carries the app's real deployment
// context.
const closeFromDocument = async (
  registration: ServiceWorkerRegistration,
  callId: string
): Promise<void> => {
  if (typeof registration.getNotifications !== 'function') return;

  try {
    const notifications = await registration.getNotifications({
      tag: INCOMING_CALL_NOTIFICATION_TAG,
    });
    notifications
      .filter((notification) => notificationBelongsToCall(notification, callId))
      .forEach((notification) => notification.close());
  } catch (error) {
    console.debug(
      '[IncomingCallNotification] getNotifications unavailable in this context',
      error
    );
  }
};

// Asks the service worker to close the notification. This is the path that
// works inside OMNIHIT: the app runs there as a cross-origin iframe, and in
// that third-party document context Chrome answers Notification API reads with
// synthetic values — registration.getNotifications() resolves to an empty list,
// so closeFromDocument finds nothing to close, which is why answering on the
// notification itself took it down (that close() runs in the worker, on
// notificationclick) while answering in the app's own UI did not. The worker is
// a plain same-origin context and holds the registration that showed the
// notification.
const requestCloseFromServiceWorker = (
  registration: ServiceWorkerRegistration,
  callId: string
): void => {
  const worker = registration.active;

  if (!worker) {
    console.debug(
      '[IncomingCallNotification] no active service worker to close the notification'
    );
    return;
  }

  const channel = new MessageChannel();
  channel.port1.onmessage = (event: MessageEvent) => {
    console.debug(
      '[IncomingCallNotification] closed by service worker',
      event.data
    );
    channel.port1.close();
  };

  worker.postMessage(buildIncomingCallNotificationCloseRequest(callId), [
    channel.port2,
  ]);
};

// Both paths are idempotent — closing an already-closed notification is a
// no-op — so running them together costs nothing and keeps the app working in
// either context.
export const closeIncomingCallNotification = async (
  callId: string
): Promise<void> => {
  const registration = await registerIncomingCallServiceWorker();
  if (!registration) return;

  requestCloseFromServiceWorker(registration, callId);
  await closeFromDocument(registration, callId);
};
